// The editor's playhead (spec docs/specs/editor/seek-renders.md): a store, so a seek, a scrub or a
// frame of Play renders only the parts that show the time, not the whole editor. A part reads it
// through `usePlayhead` or `AtPlayhead`; a handler reads `get()` when it runs.
import { useSyncExternalStore, type ReactNode } from "react";

export interface Playhead {
  get: () => number;
  /** Moves the playhead: its readers render again, when the time changed. */
  set: (ms: number) => void;
  /** Tells `listener` of each change; the function returned stops it. */
  subscribe: (listener: () => void) => () => void;
}

export function createPlayhead(ms = 0): Playhead {
  let now = ms;
  const listeners = new Set<() => void>();
  return {
    get: () => now,
    set: (next) => {
      if (Object.is(next, now)) return;
      now = next;
      // A copy: a listener may stop another as it runs (a reader unmounting).
      const told = [...listeners];
      for (const listener of told) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** The playhead's time, rendered again at each change (spec seek-renders.md, decision 1). */
export function usePlayhead(store: Playhead): number {
  return useSyncExternalStore(store.subscribe, store.get);
}

/** Renders `children` at the playhead's time: a part that shows the time, in a screen that does not. */
export function AtPlayhead(props: { store: Playhead; children: (ms: number) => ReactNode }): ReactNode {
  return props.children(usePlayhead(props.store));
}

/**
 * What a screen shows from the time without showing the time: `read` of the playhead's time,
 * its caller rendered again only when that answer changes (Split offered or not, say).
 */
export function usePlayheadWatch<T extends string | number | boolean>(store: Playhead, read: (ms: number) => T): T {
  return useSyncExternalStore(store.subscribe, () => read(store.get()));
}
